import React, { useEffect, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useParams } from "react-router-dom";
import TemplateListMenu from "./component/TemplateListMenu";
import InvoiceForm from "./component/InvoiceForm";
import getProducts from "../../api/product/getProducts";
import getStore from "../../api/store/getStore";
import getMetadata from "../../api/store/getMetadata";
import {
  isCachedProductsCurrent,
  isCachedStoreCurrent,
  readCachedStoreData,
  writeCachedMetadata,
  writeCachedProducts,
  writeCachedStore,
} from "./storeDataCache";

const buildMenuData = (productsData) => {
  const categoryMap = new Map();

  for (const product of productsData || []) {
    const categoryId = product.category_id || "";
    if (!categoryMap.has(categoryId)) {
      categoryMap.set(categoryId, []);
    }
    categoryMap.get(categoryId).push(product);
  }

  for (const group of categoryMap.values()) {
    group.sort((a, b) => {
      const ay = a.rect?.y || 0;
      const by = b.rect?.y || 0;
      if (ay === by) {
        return (a.rect?.x || 0) - (b.rect?.x || 0);
      }
      return ay - by;
    });
  }

  const products = Array.from(categoryMap.entries())
    .sort(([aId], [bId]) => aId.localeCompare(bId))
    .flatMap(([, group]) => group);

  const categories = [
    ...new Map(
      products
        .filter((product) => product.category?.trim() !== "")
        .map((product) => [
          product.category,
          { id: product.category_id, label: product.category },
        ])
    ).values(),
  ];

  return { products, categories };
};

const fetchFreshStoreData = async (storeId) => {
  const [storeResponse, productsResponse] = await Promise.all([
    getStore(storeId),
    getProducts(storeId),
  ]);

  writeCachedStore(storeId, storeResponse.store);
  writeCachedProducts(storeId, productsResponse.products || []);

  return {
    storeResponse,
    productsData: productsResponse.products || [],
  };
};

const StorePage = () => {
  const { storeId, tableNo } = useParams();
  // If tableNo is takeout, we assume it's a takeout order.
  const isTakeout = tableNo === "takeout";
  const [storeData, setStoreData] = useState({
    store: null,
    products: [],
    categories: [],
    invoice_enable: false,
    loading: true,
    error: null,
  });

  const [cartData, setCartData] = useState({
    selectedProductQuantity: {},
    selectedOptions: {},
    tableNo: tableNo,
    isTakeout: isTakeout,
    invoiceCarrier: "",
    businessNo: "",
    cart: [],
  });

  const calculateTotal = () => {
    let total = 0;
    Object.values(cartData.cart).forEach((item) => {
      total += item.subtotal;
    });
    return total;
  };

  useEffect(() => {
    let ignore = false;

    const loadStorePageData = async () => {
      try {
        const metadata = await getMetadata(storeId);
        const cached = readCachedStoreData(storeId);
        const shouldUseCachedStore =
          cached.store && isCachedStoreCurrent(cached.metadata, metadata);
        const shouldUseCachedProducts =
          cached.products && isCachedProductsCurrent(cached.metadata, metadata);

        const [storeResponse, productsData] = await Promise.all([
          shouldUseCachedStore
            ? Promise.resolve({ store: cached.store })
            : getStore(storeId).then((response) => {
                writeCachedStore(storeId, response.store);
                return response;
              }),
          shouldUseCachedProducts
            ? Promise.resolve(cached.products)
            : getProducts(storeId).then((response) => {
                const products = response.products || [];
                writeCachedProducts(storeId, products);
                return products;
              }),
        ]);

        writeCachedMetadata(storeId, metadata);
        const { products, categories } = buildMenuData(productsData);

        if (ignore) {
          return;
        }

        setStoreData((prevState) => ({
          ...prevState,
          store: storeResponse.store,
          products,
          categories,
          invoice_enable:
            storeResponse.invoice_enable ??
            storeResponse.store?.invoice_enable ??
            prevState.invoice_enable,
          loading: false,
        }));
      } catch (err) {
        try {
          const { storeResponse, productsData } = await fetchFreshStoreData(storeId);
          const { products, categories } = buildMenuData(productsData);

          if (ignore) {
            return;
          }

          setStoreData((prevState) => ({
            ...prevState,
            store: storeResponse.store,
            products,
            loading: false,
            categories,
            invoice_enable:
              storeResponse.invoice_enable ??
              storeResponse.store?.invoice_enable ??
              prevState.invoice_enable,
          }));
        } catch (freshErr) {
          if (ignore) {
            return;
          }

          setStoreData((prevState) => ({
            ...prevState,
            error: freshErr.message || err.message || "An error occurred.",
            loading: false,
          }));
        }
      }
    };

    loadStorePageData();

    return () => {
      ignore = true;
    };
  }, [storeId]);

  const handleInvoiceSubmit = (data) => {
    setCartData((prevState) => ({
      ...prevState,
      invoiceCarrier: data.invoiceCarrier,
      businessNo: data.businessNumber,
    }));
  };

  if (storeData.loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (storeData.error) {
    return (
      <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center", p: 3 }}>
        <Typography color="error" textAlign="center">
          {storeData.error}
        </Typography>
      </Box>
    );
  }

  return (
    <div style={{ overflow: "visible" }}>
      <TemplateListMenu
        store={storeData.store}
        storeId={storeId}
        tableNo={tableNo}
        categories={storeData.categories}
        products={storeData.products}
        cartData={cartData}
        setCartData={setCartData}
        calculateTotal={calculateTotal}
        invoiceSlot={
          storeData.invoice_enable ? (
            <InvoiceForm onSubmit={handleInvoiceSubmit} />
          ) : null
        }
      />
    </div>
  );
};

export default StorePage;
